import { Children, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from './internal/utils'

const Item = ({ href, onClick, className, children, ...rest }) => {
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={cn(
          'ui-breadcrumb-link text-[var(--text-muted)] hover:text-[var(--primary-color)]',
          className
        )}
        {...rest}
      >
        {children}
      </a>
    )
  }

  return (
    <span className={cn('ui-breadcrumb-link', className)} onClick={onClick} {...rest}>
      {children}
    </span>
  )
}

const Separator = ({ children = '/' }) => (
  <span className="ui-breadcrumb-separator mx-2 text-[var(--gray-500)]" aria-hidden="true">
    {children}
  </span>
)

/**
 * antd Breadcrumb. Accepts either the `items` array (v5) or `Breadcrumb.Item`
 * children (v4 style), both of which appear in this codebase.
 */
const Breadcrumb = ({ items, separator = '/', className, children, ...rest }) => {
  const { t } = useTranslation()
  const nodes = items?.length
    ? items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key -- items are positional
        <Item key={item.key ?? index} href={item.href} onClick={item.onClick}>
          {item.title ?? item.breadcrumbName}
        </Item>
      ))
    : Children.toArray(children)

  return (
    <nav aria-label={t('common.breadcrumb')} className={cn('ui-breadcrumb text-sm', className)} {...rest}>
      <ol className="flex flex-wrap items-center gap-0 p-0">
        {nodes.map((node, index) => (
          // eslint-disable-next-line react/no-array-index-key -- positional
          <Fragment key={index}>
            <li className="ui-breadcrumb-item inline-flex items-center">
              {node}
              {index < nodes.length - 1 ? <Separator>{separator}</Separator> : null}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  )
}

Breadcrumb.Item = Item
Breadcrumb.Separator = Separator

export default Breadcrumb
